import styled, { css } from "styled-components";
import { FilterState, GlobalStyleState } from "../store/chart";
import { UIButton, UIColor } from "../types";
import { Button, PrimaryButton } from "./Button";

type FiltersProps = {
  className?: string;
  styling: GlobalStyleState;
  filters: FilterState[];
  showFilters: boolean;
  additionalFilterButton?: UIButton;
  showAdditionalFilterButton: boolean;
  selectedFilterIds: string[];
  onFilterToggle: (id: string) => void;
};

export function Filters({
  className,
  styling,
  filters,
  showFilters,
  additionalFilterButton,
  showAdditionalFilterButton,
  selectedFilterIds,
  onFilterToggle,
}: FiltersProps) {
  const buttons =
    showFilters &&
    filters.map((filter) => (
      <FilterItem key={filter.id}>
        <FilterButton
          aria-pressed={selectedFilterIds.includes(filter.id)}
          onClick={() => onFilterToggle(filter.id)}
          {...styling.filterButton}
        >
          {filter.name}
        </FilterButton>
      </FilterItem>
    ));
  const additionalButton = showAdditionalFilterButton &&
    additionalFilterButton && (
      <FilterItem>
        <AdditionalFilterButton
          as="a"
          target="_blank"
          font={styling.filterButton?.font}
          href={additionalFilterButton.link}
          backgroundColor={additionalFilterButton.backgroundColor}
          borderColor={additionalFilterButton.borderColor}
          textColor={additionalFilterButton.label.color}
        >
          {additionalFilterButton.label.value}
        </AdditionalFilterButton>
      </FilterItem>
    );
  return (
    <Container className={className}>
      {buttons}
      {additionalButton}
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: ${({ theme }) => (theme.isMobile ? "flex-start" : "center")};
  align-items: center;
  margin: 0
    ${({ theme }) =>
      theme.isMobile ? `-${theme.getValue(4)}px` : `-${theme.getValue(8)}px`};
`;

const FilterItem = styled.div`
  padding: ${({ theme }) =>
    theme.isMobile ? theme.getValue(4) : theme.getValue(8)}px;
  ${({ theme }) =>
    theme.isMobile
      ? css`
          width: 50%;
        `
      : css`
          min-width: ${theme.getValue(260)}px;
        `};
`;

const FilterButton = styled(PrimaryButton)<{
  selected?: {
    textColor?: UIColor;
    backgroundColor?: UIColor;
    borderColor?: UIColor;
  };
}>`
  display: block;
  width: 100%;
  white-space: ${({ theme }) => (theme.isMobile ? "wrap" : "nowrap")};
  color: ${({ theme, textColor }) => textColor?.hex || theme.colors.green2};
  background: ${({ theme, backgroundColor }) =>
    backgroundColor?.hex || theme.colors.green4};
  border-color: ${({ theme, borderColor }) =>
    borderColor?.hex || theme.colors.green2};

  &[aria-pressed="true"] {
    color: ${({ theme, selected }) =>
      selected?.textColor?.hex || theme.colors.white};
    border-color: ${({ theme, selected }) =>
      selected?.borderColor?.hex || theme.colors.green2};
    background: ${({ theme, selected }) =>
      selected?.backgroundColor?.hex || theme.colors.green2};
  }
`;

export const AdditionalFilterButton = styled(Button)<{
  textColor?: UIColor;
  backgroundColor?: UIColor;
  borderColor?: UIColor;
}>`
  display: block;
  width: 100%;
  white-space: ${({ theme }) => (theme.isMobile ? "wrap" : "nowrap")};
  text-decoration: none;
  color: ${({ theme, textColor }) => textColor?.hex || theme.colors.black};
  background: ${({ theme, backgroundColor }) =>
    backgroundColor?.hex || theme.colors.white};
  border: 3px solid
    ${({ theme, borderColor }) => borderColor?.hex || theme.colors.white};
`;
